import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import { accountErrorMessage } from '../../utils/accountErrors';
import { isCentralRole, isOperatorAdminRole, isOperatorRole } from './authSlice';


const getCurrentUser = (state) => state.auth?.user || null;

const getOperatorScope = (state) => {
  const currentUser = getCurrentUser(state);
  return isOperatorRole(currentUser) ? currentUser.operatorId || null : null;
};

const assertOperatorAccess = (currentUser, data, entityName = 'Datensatz') => {
  if (isCentralRole(currentUser)) return;
  if (!isOperatorRole(currentUser) || !currentUser.operatorId) {
    throw new Error('Keine Berechtigung');
  }
  if (data.operatorId !== currentUser.operatorId) {
    throw new Error(`${entityName} gehoert zu einem anderen Unternehmer`);
  }
};

const assertCanManageVehicles = (currentUser) => {
  if (isCentralRole(currentUser) || isOperatorAdminRole(currentUser)) return;
  throw new Error('Nur zentrale Rollen und Unternehmer-Admins duerfen Fahrzeuge verwalten');
};

// Async thunks
export const fetchVehicles = createAsyncThunk(
  'vehicles/fetchVehicles',
  async (_, { getState, rejectWithValue }) => {
    try {
      const operatorScope = getOperatorScope(getState());
      const vehiclesQuery = operatorScope
        ? query(
            collection(db, 'Vehicles'),
            where('operatorId', '==', operatorScope),
            orderBy('model', 'asc')
          )
        : query(
            collection(db, 'Vehicles'),
            orderBy('model', 'asc')
          );
      
      const querySnapshot = await getDocs(vehiclesQuery);
      const vehicles = [];
      
      for (const vehicleDoc of querySnapshot.docs) {
        const vehicleData = vehicleDoc.data();
        const vehicleId = vehicleDoc.id;
        
        // Aktuellen Fahrer abrufen, falls zugewiesen
        let driverData = null;
        if (vehicleData.driverId) {
          const userDoc = await getDoc(doc(db, 'users', vehicleData.driverId));
          
          if (userDoc.exists() && userDoc.data().isDriver) {
            driverData = {
              id: vehicleData.driverId,
              ...userDoc.data(),
              user: {
                name: userDoc.data().name,
                email: userDoc.data().email,
                phone: userDoc.data().phone || '',
                profile_picture: userDoc.data().profilePic || ''
              }
            };
          }
        }
        
        // UI-Status: Reservefahrzeug (active === false) → 'inactive', sonst nach inUse
        const uiStatus = vehicleData.active === false ? 'inactive' : vehicleData.inUse ? 'in_use' : 'active';
        
        vehicles.push({
          id: vehicleId,
          vehicleNumber: vehicleData.vehicleNumber || '',
          model: vehicleData.model,
          licensePlate: vehicleData.licensePlate, // Verwendung des korrekt benannten Feldes
          phoneNumber: vehicleData.phoneNumber || '',
          inUse: vehicleData.inUse || false,
          status: uiStatus, // UI-Status für die Anzeige
          active: vehicleData.active !== false,
          lastActivity: vehicleData.lastActivityTimestamp || null,
          vehicleType: vehicleData.vehicleType || 1, // Standardmäßig PKW, wenn nicht angegeben
          specialCapabilities: vehicleData.specialCapabilities || (vehicleData.specialVehicleType ? [vehicleData.specialVehicleType] : []),
          organizationId: vehicleData.organizationId || null,
          operatorId: vehicleData.operatorId || null,
          current_driver: driverData,
          // Zusätzliche Felder, falls in Zukunft benötigt
          year: vehicleData.year,
          capacity: vehicleData.capacity || 0,
          maintenanceHistory: vehicleData.maintenanceHistory || [], // Verwendung des korrekten Feldnamens
          totalDistance: vehicleData.totalDistance || 0 // Verwendung des korrekten Feldnamens
        });
      }

      return vehicles;
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchVehicleById = createAsyncThunk(
  'vehicles/fetchVehicleById',
  async (vehicleId, { getState, rejectWithValue }) => {
    try {
      const vehicleDoc = await getDoc(doc(db, 'Vehicles', vehicleId));
      
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }
      
      const vehicleData = vehicleDoc.data();
      const currentUser = getCurrentUser(getState());
      assertOperatorAccess(currentUser, vehicleData, 'Fahrzeug');
      
      // Aktuellen Fahrer abrufen, falls zugewiesen
      let driverData = null;
      if (vehicleData.driverId) {
        const userDoc = await getDoc(doc(db, 'users', vehicleData.driverId));
        
        if (userDoc.exists() && userDoc.data().isDriver) {
          driverData = {
            id: vehicleData.driverId,
            ...userDoc.data(),
            user: {
              name: userDoc.data().name,
              email: userDoc.data().email,
              phone: userDoc.data().phone || '',
              profile_picture: userDoc.data().profilePic || ''
            }
          };
        }
      }
      
      // UI-Status: Reservefahrzeug (active === false) → 'inactive', sonst nach inUse
      const uiStatus = vehicleData.active === false ? 'inactive' : vehicleData.inUse ? 'in_use' : 'active';
      
      return {
        id: vehicleId,
        vehicleNumber: vehicleData.vehicleNumber || '',
        model: vehicleData.model,
        licensePlate: vehicleData.licensePlate,
        phoneNumber: vehicleData.phoneNumber || '',
        inUse: vehicleData.inUse || false,
        status: uiStatus,
        active: vehicleData.active !== false,
        lastActivity: vehicleData.lastActivityTimestamp || null,
        vehicleType: vehicleData.vehicleType || 1,
        specialCapabilities: vehicleData.specialCapabilities || (vehicleData.specialVehicleType ? [vehicleData.specialVehicleType] : []),
        paymentMethods: vehicleData.paymentMethods || 1,
        organizationId: vehicleData.organizationId || null,
        operatorId: vehicleData.operatorId || null,
        inspectionDate: vehicleData.inspectionDate || null,
        tuvDate: vehicleData.tuvDate || null,
        documents: vehicleData.documents || [],
        current_driver: driverData,
        year: vehicleData.year,
        capacity: vehicleData.capacity || 0,
        maintenanceHistory: vehicleData.maintenanceHistory || [],
        totalDistance: vehicleData.totalDistance || 0
      };
    } catch (error) {
      console.error('Error fetching vehicle by ID:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fahrzeug anlegen — über die CF `createVehicleWithQuota`.
 *
 * Die Kontingentprüfung lief bisher hier im Thunk und war damit umgehbar; die
 * Firestore-Rules erlaubten `create` auf `Vehicles` sogar jedem Fahrer. Beides
 * ist geschlossen: Die Rules sperren Client-Schreiber, und die CF zählt den
 * Bestand transaktional gegen das Kontingent des Unternehmens bzw. gegen den
 * Lizenzrahmen. Die Rolle des Aufrufers und die operatorId prüft ebenfalls die
 * CF — ein Unternehmer-Konto legt immer nur für das eigene Unternehmen an.
 */
export const createVehicle = createAsyncThunk(
  'vehicles/createVehicle',
  async (vehicleData, { rejectWithValue, getState }) => {
    try {
      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);

      const createVehicleFn = httpsCallable(functions, 'createVehicleWithQuota');
      const result = await createVehicleFn({ vehicle: vehicleData });

      return { id: result.data?.id, ...(result.data?.vehicle || {}) };
    } catch (error) {
      console.error('Error creating vehicle:', error);
      return rejectWithValue(accountErrorMessage(error, 'Das Fahrzeug konnte nicht angelegt werden.'));
    }
  }
);

/**
 * Fahrzeug als Reserve deaktivieren bzw. wieder aktivieren — über die CF
 * `setVehicleActive`. Deaktivierte Fahrzeuge zählen nicht gegen Kontingent/
 * Lizenzrahmen; beim Aktivieren prüft die CF das Kontingent erneut. Die Rules
 * sperren das Feld `active` für Client-Schreiber.
 */
export const setVehicleActive = createAsyncThunk(
  'vehicles/setVehicleActive',
  async ({ id, active }, { rejectWithValue, getState }) => {
    try {
      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);

      const setVehicleActiveFn = httpsCallable(functions, 'setVehicleActive');
      await setVehicleActiveFn({ vehicleId: id, active: active === true });
      return { id, active: active === true };
    } catch (error) {
      console.error('Error toggling vehicle active state:', error);
      return rejectWithValue(accountErrorMessage(
        error,
        active ? 'Das Fahrzeug konnte nicht aktiviert werden.' : 'Das Fahrzeug konnte nicht deaktiviert werden.'
      ));
    }
  }
);

export const updateVehicle = createAsyncThunk(
  'vehicles/updateVehicle',
  async ({ id, ...vehicleData }, { getState, rejectWithValue }) => {
    try {
      const vehicleRef = doc(db, 'Vehicles', id);
      const vehicleDoc = await getDoc(vehicleRef);
      
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }

      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);
      assertOperatorAccess(currentUser, vehicleDoc.data(), 'Fahrzeug');
      
      // Update-Daten vorbereiten
      const updateData = {};

      if (vehicleData.vehicleNumber !== undefined) {
        const normalizedVehicleNumber = (vehicleData.vehicleNumber || '').trim();
        if (normalizedVehicleNumber) {
          const numberSnapshot = await getDocs(query(
            collection(db, 'Vehicles'),
            where('vehicleNumber', '==', normalizedVehicleNumber)
          ));
          const duplicate = numberSnapshot.docs.find(docSnapshot => docSnapshot.id !== id);
          if (duplicate) {
            throw new Error(`Fahrzeugnummer ${normalizedVehicleNumber} ist bereits vergeben`);
          }
        }
        updateData.vehicleNumber = normalizedVehicleNumber;
      }
      if (vehicleData.model) updateData.model = vehicleData.model;
      if (vehicleData.licensePlate) updateData.licensePlate = vehicleData.licensePlate;
      if (vehicleData.phoneNumber !== undefined) updateData.phoneNumber = (vehicleData.phoneNumber || '').trim();
      if (vehicleData.year) updateData.year = vehicleData.year;
      if (vehicleData.capacity) updateData.capacity = vehicleData.capacity;
      if (vehicleData.features) updateData.features = vehicleData.features;
      if (vehicleData.vehicleType !== undefined) updateData.vehicleType = vehicleData.vehicleType;
      if (vehicleData.specialCapabilities !== undefined) updateData.specialCapabilities = vehicleData.specialCapabilities;
      if (vehicleData.paymentMethods !== undefined) updateData.paymentMethods = vehicleData.paymentMethods;
      if (vehicleData.status !== undefined) updateData.status = vehicleData.status;
      if (vehicleData.inspectionDate !== undefined) updateData.inspectionDate = vehicleData.inspectionDate;
      if (vehicleData.tuvDate !== undefined) updateData.tuvDate = vehicleData.tuvDate;
      if (vehicleData.documents !== undefined) updateData.documents = vehicleData.documents;
      
      // Wenn ein Fahrer zugewiesen werden soll
      if (vehicleData.driverId) {
        const driverDoc = await getDoc(doc(db, 'users', vehicleData.driverId));
        if (!driverDoc.exists() || !driverDoc.data().isDriver) {
          throw new Error('Fahrer nicht gefunden');
        }
        if ((vehicleDoc.data().operatorId || null) !== (driverDoc.data().operatorId || null)) {
          throw new Error('Fahrer und Fahrzeug muessen zum selben Unternehmer gehoeren');
        }
        updateData.driverId = vehicleData.driverId;
        updateData.inUse = true;
      }
      
      // Wenn der Fahrer entfernt werden soll
      if (vehicleData.driverId === null) {
        updateData.driverId = null;
        updateData.inUse = false;
      }
      
      // Fahrzeugdaten aktualisieren
      await updateDoc(vehicleRef, {
        ...updateData,
        lastActivityTimestamp: Timestamp.now()
      });
      
      // Aktualisierte Daten direkt vom Server abrufen
      const updatedVehicleDoc = await getDoc(vehicleRef);
      const updatedVehicleData = updatedVehicleDoc.data();
      
      // Driverdata holen wenn nötig
      let driverData = null;
      if (updatedVehicleData.driverId) {
        const userDoc = await getDoc(doc(db, 'users', updatedVehicleData.driverId));
        
        if (userDoc.exists() && userDoc.data().isDriver) {
          driverData = {
            id: updatedVehicleData.driverId,
            ...userDoc.data(),
            user: {
              name: userDoc.data().name,
              email: userDoc.data().email,
              phone: userDoc.data().phone || '',
              profile_picture: userDoc.data().profilePic || ''
            }
          };
        }
      }
      
      // UI-Status: Reservefahrzeug (active === false) → 'inactive', sonst nach inUse
      const uiStatus = updatedVehicleData.active === false ? 'inactive' : updatedVehicleData.inUse ? 'in_use' : 'active';
      
      return {
        id,
        vehicleNumber: updatedVehicleData.vehicleNumber || '',
        model: updatedVehicleData.model,
        licensePlate: updatedVehicleData.licensePlate,
        inUse: updatedVehicleData.inUse || false,
        status: uiStatus,
        active: updatedVehicleData.active !== false,
        lastActivity: updatedVehicleData.lastActivityTimestamp || null,
        vehicleType: updatedVehicleData.vehicleType || 1,
        specialCapabilities: updatedVehicleData.specialCapabilities || (updatedVehicleData.specialVehicleType ? [updatedVehicleData.specialVehicleType] : []),
        paymentMethods: updatedVehicleData.paymentMethods || 1,
        organizationId: updatedVehicleData.organizationId || null,
        operatorId: updatedVehicleData.operatorId || null,
        inspectionDate: updatedVehicleData.inspectionDate || null,
        tuvDate: updatedVehicleData.tuvDate || null,
        documents: updatedVehicleData.documents || [],
        current_driver: driverData,
        year: updatedVehicleData.year,
        capacity: updatedVehicleData.capacity || 0,
        maintenanceHistory: updatedVehicleData.maintenanceHistory || [],
        totalDistance: updatedVehicleData.totalDistance || 0
      };
    } catch (error) {
      console.error('Error updating vehicle:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteVehicle = createAsyncThunk(
  'vehicles/deleteVehicle',
  async (id, { getState, rejectWithValue }) => {
    try {
      const vehicleRef = doc(db, 'Vehicles', id);
      const vehicleDoc = await getDoc(vehicleRef);
      
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }
      
      const vehicleData = vehicleDoc.data();
      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);
      assertOperatorAccess(currentUser, vehicleData, 'Fahrzeug');
      
      // Prüfen, ob Fahrzeug aktuell in Benutzung ist
      if (vehicleData.inUse && vehicleData.driverId) {
        throw new Error('Fahrzeug ist aktuell einem Fahrer zugewiesen und kann nicht gelöscht werden');
      }
      
      // Fahrzeug löschen
      await deleteDoc(vehicleRef);
      
      return id;
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const releaseVehicle = createAsyncThunk(
  'vehicles/releaseVehicle',
  async (id, { getState, rejectWithValue }) => {
    try {
      const vehicleRef = doc(db, 'Vehicles', id);
      const vehicleDoc = await getDoc(vehicleRef);
      
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }
      
      const vehicleData = vehicleDoc.data();
      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);
      assertOperatorAccess(currentUser, vehicleData, 'Fahrzeug');
      
      // Prüfen, ob Fahrzeug einem Fahrer zugewiesen ist
      if (!vehicleData.inUse || !vehicleData.driverId) {
        throw new Error('Fahrzeug ist keinem Fahrer zugewiesen');
      }

      // Fahrer-Referenz holen
      const driverId = vehicleData.driverId;
      const driverRef = doc(db, 'users', driverId);
      const driverDoc = await getDoc(driverRef);
      
      if (!driverDoc.exists()) {
        throw new Error('Fahrer nicht gefunden');
      }
      
      // Remote-Logout für den Fahrer aktivieren
      await updateDoc(driverRef, {
        car: null,
        forceLogout: true,
        forceLogoutTimestamp: Timestamp.now(),
        lastLogoutCheck: null,
        isOnline: false,
        isAvailable: false,
      });

      // driverLocations ebenfalls aktualisieren (für Dispatcher-Anzeige und Wartezeitberechnung)
      try {
        const driverLocRef = doc(db, 'driverLocations', driverId);
        const driverLocSnap = await getDoc(driverLocRef);
        if (driverLocSnap.exists()) {
          await updateDoc(driverLocRef, { isAvailable: false });
        }
      } catch (locErr) {
        console.warn('driverLocations Update fehlgeschlagen:', locErr);
      }
      
      // Batch-Update für Fahrzeug
      await updateDoc(vehicleRef, {
        inUse: false,
        driverId: null,
        lastActivityTimestamp: Timestamp.now()
      });
      
      // Aktualisierte Fahrzeugdaten zurückgeben
      const updatedVehicleDoc = await getDoc(vehicleRef);
      const updatedVehicleData = updatedVehicleDoc.data();
      
      // Nach der Freigabe ist inUse immer false, also ist der UI-Status 'active'
      return {
        id,
        vehicleNumber: updatedVehicleData.vehicleNumber || '',
        model: updatedVehicleData.model,
        licensePlate: updatedVehicleData.licensePlate,
        inUse: false,
        status: updatedVehicleData.active === false ? 'inactive' : 'active',
        active: updatedVehicleData.active !== false,
        lastActivity: updatedVehicleData.lastActivityTimestamp || null,
        vehicleType: updatedVehicleData.vehicleType || 1,
        specialCapabilities: updatedVehicleData.specialCapabilities || (updatedVehicleData.specialVehicleType ? [updatedVehicleData.specialVehicleType] : []),
        current_driver: null,
        year: updatedVehicleData.year,
        capacity: updatedVehicleData.capacity || 0,
        maintenanceHistory: updatedVehicleData.maintenanceHistory || [],
        totalDistance: updatedVehicleData.totalDistance || 0
      };
    } catch (error) {
      console.error('Error releasing vehicle:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const addMaintenanceRecord = createAsyncThunk(
  'vehicles/addMaintenanceRecord',
  async ({ vehicleId, record }, { rejectWithValue, dispatch }) => {
    try {
      const vehicleRef = doc(db, 'Vehicles', vehicleId);
      const vehicleDoc = await getDoc(vehicleRef);
      
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }
      
      const vehicleData = vehicleDoc.data();
      const maintenanceHistory = vehicleData.maintenanceHistory || [];
      
      const maintenanceRecord = {
        ...record,
        date: Timestamp.now(),
        id: Date.now().toString()
      };
      
      await updateDoc(vehicleRef, {
        maintenanceHistory: [...maintenanceHistory, maintenanceRecord],
        lastMaintenance: Timestamp.now(),
        lastActivityTimestamp: Timestamp.now()
      });
      
      // Aktualisierte Fahrzeugdaten abrufen
      const result = await dispatch(fetchVehicleById(vehicleId));
      return result.payload;
    } catch (error) {
      console.error('Error adding maintenance record:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Aktiver Listener-Speicher außerhalb des Reducer, um Cleanup zu ermöglichen
let vehiclesStatusListener = null;

const initialState = {
  vehicles: [],
  currentVehicle: null,
  isLoading: false,
  error: null
};

const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  reducers: {
    clearVehiclesError: (state) => {
      state.error = null;
    },
    clearCurrentVehicle: (state) => {
      state.currentVehicle = null;
    },
    updateVehicleRealtime: (state, action) => {
      const vehicleData = action.payload;
      const index = state.vehicles.findIndex(vehicle => vehicle.id === vehicleData.id);
      
      if (index !== -1) {
        // Nur die aktualisierten Felder übernehmen
        state.vehicles[index] = {
          ...state.vehicles[index],
          ...vehicleData
        };
      }
      
      // Auch das aktuelle Fahrzeug aktualisieren, falls es dasselbe ist
      if (state.currentVehicle && state.currentVehicle.id === vehicleData.id) {
        state.currentVehicle = {
          ...state.currentVehicle,
          ...vehicleData
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Reserve (de)aktivieren — Fehler meldet die aufrufende Komponente
      // selbst (unwrap), damit keine Seite in den Fehlerzustand kippt.
      .addCase(setVehicleActive.fulfilled, (state, action) => {
        const { id, active } = action.payload;
        const apply = (vehicle) => {
          vehicle.active = active;
          vehicle.status = !active ? 'inactive' : vehicle.inUse ? 'in_use' : 'active';
        };
        const vehicle = state.vehicles.find(v => v.id === id);
        if (vehicle) apply(vehicle);
        if (state.currentVehicle?.id === id) apply(state.currentVehicle);
      })
      // Fetch all vehicles
      .addCase(fetchVehicles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicles = action.payload;
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch vehicle by ID
      .addCase(fetchVehicleById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVehicleById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentVehicle = action.payload;
      })
      .addCase(fetchVehicleById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create vehicle
      .addCase(createVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicles.push(action.payload);
        state.currentVehicle = action.payload;
      })
      .addCase(createVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update vehicle
      .addCase(updateVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.vehicles.findIndex(vehicle => vehicle.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle && state.currentVehicle.id === action.payload.id) {
          state.currentVehicle = action.payload;
        }
      })
      .addCase(updateVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete vehicle
      .addCase(deleteVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicles = state.vehicles.filter(vehicle => vehicle.id !== action.payload);
        if (state.currentVehicle && state.currentVehicle.id === action.payload) {
          state.currentVehicle = null;
        }
      })
      .addCase(deleteVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add maintenance record
      .addCase(addMaintenanceRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addMaintenanceRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.vehicles.findIndex(vehicle => vehicle.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle && state.currentVehicle.id === action.payload.id) {
          state.currentVehicle = action.payload;
        }
      })
      .addCase(addMaintenanceRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Release vehicle
      .addCase(releaseVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(releaseVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.vehicles.findIndex(vehicle => vehicle.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle && state.currentVehicle.id === action.payload.id) {
          state.currentVehicle = action.payload;
        }
      })
      .addCase(releaseVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

// Thunk zum Starten des Echtzeit-Listeners für Fahrzeug-Status
export const startVehiclesStatusListener = () => async (dispatch, getState) => {
  console.log('Starte Echtzeit-Listener für Fahrzeug-Status...');
  
  // Existierenden Listener entfernen, falls vorhanden
  if (vehiclesStatusListener) {
    console.log('Beende alten Fahrzeug-Listener...');
    vehiclesStatusListener();
    vehiclesStatusListener = null;
  }
  
  // Spezielle Anpassungen für Produktionsumgebungen
  const PROD_ENV = process.env.NODE_ENV === 'production';
  
  // Helper für zuverlässigere Echtzeit-Updates in Produktionsumgebungen
  const enhanceProductionReliability = (callback) => {
    return (...args) => {
      // In Produktionsumgebungen zusätzliche Verzögerung für stabilere Verbindungen
      if (PROD_ENV) {
        console.log('VehiclesSlice: Produktionsoptimierungen werden angewendet');
        setTimeout(() => callback(...args), 500);
      } else {
        callback(...args);
      }
    };
  };
  
  // Listener für die Fahrzeugsammlung einrichten
  const operatorScope = getOperatorScope(getState());
  const vehiclesQuery = operatorScope
    ? query(
        collection(db, 'Vehicles'),
        where('operatorId', '==', operatorScope),
        orderBy('model', 'asc')
      )
    : query(
        collection(db, 'Vehicles'),
        orderBy('model', 'asc')
      );
  
  // In Produktionsumgebungen robusteres Error-Handling
  const errorHandler = (error) => {
    console.error('Fehler im Fahrzeug-Status-Listener:', error);
    
    if (PROD_ENV) {
      console.log('VehiclesSlice: Versuche Fahrzeug-Listener neu zu starten...');
      // Im Produktionsmodus nach Fehlern neu starten
      setTimeout(() => {
        try {
          dispatch(startVehiclesStatusListener());
          console.log('VehiclesSlice: Listener erfolgreich neu gestartet');
        } catch (e) {
          console.error('VehiclesSlice: Neustart des Listeners fehlgeschlagen:', e);
        }
      }, 2000);
    }
  };
  
  // Wrapper für zuverlässigere Callbacks in Produktionsumgebungen
  const processVehicleChanges = enhanceProductionReliability(async (snapshot) => {
    for (const change of snapshot.docChanges()) {
        const vehicleData = change.doc.data();
        const vehicleId = change.doc.id;
        
        // Nur bei neuen oder geänderten Dokumenten
        if (change.type === 'added' || change.type === 'modified') {
          console.log(`Echtzeit-Update für Fahrzeug ${vehicleId}:`, vehicleData.inUse, vehicleData.driverId);
          
          // Aktuellen Fahrer abrufen, falls zugewiesen
          let driverData = null;
          if (vehicleData.driverId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', vehicleData.driverId));
              
              if (userDoc.exists() && userDoc.data().isDriver) {
                driverData = {
                  id: vehicleData.driverId,
                  ...userDoc.data(),
                  user: {
                    name: userDoc.data().name,
                    email: userDoc.data().email,
                    phone: userDoc.data().phone || '',
                    profile_picture: userDoc.data().profilePic || ''
                  }
                };
              }
            } catch (error) {
              console.error(`Fehler beim Abrufen des Fahrers für Fahrzeug ${vehicleId}:`, error);
            }
          }
          
          // UI-Status: Reservefahrzeug (active === false) → 'inactive', sonst nach inUse
          const uiStatus = vehicleData.active === false ? 'inactive' : vehicleData.inUse ? 'in_use' : 'active';
          
          // Relevante Statusdaten extrahieren und an den Store senden
          dispatch({
            type: 'vehicles/updateVehicleRealtime',
            payload: {
              id: vehicleId,
              inUse: vehicleData.inUse || false,
              status: uiStatus,
              active: vehicleData.active !== false,
              current_driver: driverData,
              organizationId: vehicleData.organizationId || null,
              operatorId: vehicleData.operatorId || null,
              lastActivity: vehicleData.lastActivityTimestamp || null
            }
          });
        }
      }
    
    if (PROD_ENV) {
      console.log('VehiclesSlice: Fahrzeug-Updates verarbeitet', 
        {timestamp: new Date().toISOString(), changes: snapshot.docChanges().length});
    }
  });
  
  vehiclesStatusListener = onSnapshot(
    vehiclesQuery,
    {
      includeMetadataChanges: PROD_ENV // In Produktion auch Metadaten-Updates beobachten
    },
    processVehicleChanges,
    errorHandler
  );
  
  if (PROD_ENV) {
    console.log('VehiclesSlice: Echtzeit-Listener erfolgreich gestartet (Produktionsmodus)');
  }
  
  return () => {
    // Cleanup-Funktion zurückgeben
    if (vehiclesStatusListener) {
      vehiclesStatusListener();
      vehiclesStatusListener = null;
    }
  };
};

// Thunk zum Stoppen des Echtzeit-Listeners
export const stopVehiclesStatusListener = () => () => {
  console.log('Stoppe Echtzeit-Listener für Fahrzeug-Status...');
  if (vehiclesStatusListener) {
    vehiclesStatusListener();
    vehiclesStatusListener = null;
  }
};

export const { clearVehiclesError, clearCurrentVehicle, updateVehicleRealtime } = vehiclesSlice.actions;
export default vehiclesSlice.reducer;
